/* eslint-disable no-param-reassign */
import cx from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { usePopper } from 'react-popper';
import { Key } from 'w3c-keys';
import { cloneChildren } from '../_utils';
export const PopoverFlipUtil = (targetRef, styles, { align, contentWidth, showAlignArrow, isVisible, content, popperRef, enableScroll, onClickOutside, bottomOffset = 0, leftOffset = 0, className, isPositionFixed = false, onEscape, isReferenceVisible, autoFocusOnVisible = false, ...restProps }, flipTooltipTemplate) => {
    const arrowRef = useRef(null);
    const popperRefElement = useRef(null);
    const target = targetRef && targetRef.current ? targetRef.current : targetRef;
    const popper = popperRef && popperRef.current
        ? popperRef.current
        : popperRefElement.current;
    const [istargetready, setIsTargetReady] = useState(0);
    const dropdownWidthModifier = useMemo(() => ({
        name: 'dropdownWidthModifier',
        enabled: contentWidth !== undefined,
        fn: ({ state }) => {
            state.styles.popper.minWidth = `${state.rects.reference.width}px`;
            switch (contentWidth) {
                case 'singleSelectWidth':
                    state.styles.popper.maxWidth = '240px';
                    break;
                case 'multiSelectWidth':
                    state.styles.popper.maxWidth = '320px';
                    break;
                case 'singleSelectWithSearchBarWidth':
                    state.styles.popper.width = '240px';
                    break;
                case 'multiSelectWithSearchBarWidth':
                    state.styles.popper.width = '320px';
                    break;
                case 'targetWidth':
                    state.styles.popper.width = `${state.rects.reference.width}px`;
                    break;
                default:
            }
        },
        phase: 'beforeWrite',
        requires: ['computeStyles'],
    }), [contentWidth]);
    const emptyTooltipTemplate = '<div></div>';
    const flipPopoverProps = { placement: align };
    let bottomOffsetProp = bottomOffset;
    let flipPopoverModifierProps = [];
    // When flipTooltipTemplate is available, the popover should flip from tooltip to context menu.
    // After flipping the popover, removing the tooltipTemplate from window object.
    if (flipTooltipTemplate) {
        // Set the popover's placement to top with a fixed positioning strategy.
        // This is necessary because the tooltip is always displayed above the target,
        // which enhances the effectiveness of the flip effect when transitioning to the context menu.
        flipPopoverProps.placement = 'top';
        flipPopoverProps.strategy = 'fixed';
        bottomOffsetProp = 12;
        // - Prevents overflow along an alternative axis.
        // - Enables flip without fallback placements, ensuring it remains in view.
        flipPopoverModifierProps = [
            {
                name: 'preventOverflow',
                options: {
                    altAxis: true,
                },
            },
            {
                name: 'flip',
                options: {
                    fallbackPlacements: [],
                },
            },
        ];
    }
    const { styles: style, attributes, update } = usePopper(target, popper, {
        ...flipPopoverProps,
        modifiers: [
            { name: 'arrow', options: { element: arrowRef.current } },
            {
                name: 'offset',
                enabled: true,
                options: {
                    offset: [leftOffset, bottomOffsetProp],
                },
            },
            dropdownWidthModifier,
        ],
        ...flipPopoverModifierProps,
    });
    const updatePopperDebounce = useCallback(_.debounce((updatePopper) => {
        updatePopper();
    }, 50), []);
    useEffect(() => {
        if (isVisible) {
            window.dispatchEvent(new Event('resize'));
        }
    }, [isVisible]);
    useEffect(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const onEscapeHandler = (event) => {
            if (isVisible && onEscape && event.key === Key.Escape) {
                onEscape(event);
            }
        };
        document.addEventListener('keydown', onEscapeHandler);
        // Delaying the focus to avoid smooth transition effect while flipping the tooltip to context menu.
        if (autoFocusOnVisible &&
            isVisible &&
            (popperRef || popperRefElement) &&
            (popperRef || popperRefElement).current) {
            if (flipTooltipTemplate) {
                setTimeout(() => {
                    (popperRef || popperRefElement).current.focus();
                }, 500);
            }
            else {
                (popperRef || popperRefElement).current.focus();
            }
        }
        if (flipTooltipTemplate &&
            window.tooltipTemplate &&
            window.tooltipTemplate !== emptyTooltipTemplate) {
            // A delay is needed to display the tooltip before flipping to the popover context menu
            // by applying the flipshow class. This ensures a smooth transition effect.
            // The tooltipTemplate is removed from the window object to prevent it from flipping again.
            setTimeout(() => {
                const popoverClass = document.getElementsByClassName(styles.in);
                if (popoverClass.length > 0) {
                    const flipCardDiv = popoverClass[0].children[0];
                    if (flipCardDiv) {
                        flipCardDiv.classList.add(styles.flipshow);
                        // A timeout is required to allow the tooltip to complete the context menu flip.
                        // Setting the tooltipTemplate to an empty value prevents repeated flipping
                        // and avoids changes in the popover's position within the liveboard.
                        // This issue occurs when the context menu is opened, and the mouse is moved out and back over the chart,
                        // triggering a redraw. During the redraw, an null tooltip template would result in an incorrect popover position.
                        // The empty tooltip template ensures the position change behavior is avoided.
                        setTimeout(() => {
                            window.tooltipTemplate = emptyTooltipTemplate;
                        }, 50);
                    }
                }
            }, 50);
        }
        return () => {
            document.removeEventListener('keydown', onEscapeHandler);
            if (flipTooltipTemplate) {
                const popoverClass = document.getElementsByClassName(styles.in);
                if (popoverClass.length > 0) {
                    const flipCardDiv = popoverClass[0].children[0];
                    if (flipCardDiv) {
                        flipCardDiv.classList.remove(styles.flipshow);
                    }
                }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, onEscape]);
    useEffect(() => {
        /**
         * Call "onClickOutside" callback when the popover is open and
         * you click anywhere outside popover and target
         */
        const handleDocumentClick = (event) => {
            if (onClickOutside && isVisible) {
                const isInsidePopper = popper && popper.contains(event.target);
                const isInsideTarget = target && target.contains(event.target);
                // Note: Must be false (not undefined or null)
                if (isInsidePopper === false && isInsideTarget === false) {
                    onClickOutside(event);
                }
            }
        };
        document.addEventListener('mousedown', handleDocumentClick);
        return () => {
            document.removeEventListener('mousedown', handleDocumentClick);
        };
    }, [popper, target, isVisible, onClickOutside]);
    useEffect(() => {
        if (update) {
            updatePopperDebounce(update);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content]);
    /* This is used to fix SCAL-78710 where the Action Menu inside popover goes outside the screen. With this extra rerender after target is ready,
       the refs inside Menu component are updated with the correct values. */
    useEffect(() => {
        if (target && target.getBoundingClientRect && !istargetready) {
            setIsTargetReady(1);
        }
    }, [target, istargetready]);
    /* This condition is to check if the window is resized and update the alignment of arrow */
    const updatedArrowAlignment = attributes.popper && attributes.popper['data-popper-placement']
        ? attributes.popper['data-popper-placement']
        : align;
    /**
     * Since the popover contains the tooltip template component,
     * we need to wait for all required elements to load to get the position; otherwise,
     * the tooltip appears at the 0th position for a split second before rendering actual position.
     */
    const shouldShowPopover = () => {
        if (flipTooltipTemplate) {
            return isVisible && target && popper;
        }
        return isVisible;
    };
    const positioncss = cx(className, styles.positionable, { [styles[updatedArrowAlignment]]: showAlignArrow }, { [styles.enableScroll]: enableScroll }, shouldShowPopover() ? styles.in : styles.out, { [styles.positionFixed]: isPositionFixed }, { [styles.referenceVisible]: isReferenceVisible }, { [styles.positionableFlip]: flipTooltipTemplate });
    const ariaProps = {
        'aria-labelledby': restProps['aria-labelledby'],
        'aria-hidden': restProps['aria-hidden'],
        'aria-orientation': restProps['aria-orientation'],
    };
    const getPopoverContent = () => (React.createElement(React.Fragment, null,
        cloneChildren(content, { istargetready }),
        showAlignArrow && (React.createElement("div", { ref: arrowRef, style: style.arrow, className: styles.arrow }))));
    // Flip the tooltip to context menu when flipTooltipTemplate is available.
    // It needs both front and back side of the card to flip.
    // The front side displays the tooltip, while the back side shows the context menu.
    // Adding the flipshow class will toggle the display to flip from the tooltip to the context menu.
    const renderPopoverContent = () => {
        return (React.createElement("div", { className: positioncss, ref: popperRef || popperRefElement, style: style.popper, ...attributes.popper, tabIndex: -1, "data-testid": "popover-container", ...ariaProps }, flipTooltipTemplate ? (React.createElement("div", { className: styles.flipCard },
            React.createElement("div", { className: styles.flipCardInner },
                React.createElement("div", { className: styles.flipCardFront }, flipTooltipTemplate),
                React.createElement("div", { className: styles.flipCardBack }, getPopoverContent())))) : (React.createElement(React.Fragment, null, getPopoverContent()))));
    };
    return {
        renderPopoverContent,
    };
};
//# sourceMappingURL=popover-flip-utils.js.map